import { useState, useEffect } from "react";

export const API_BASE     = process.env.REACT_APP_API_URL   || "/api/go";
export const MAGMA_THESIS = process.env.REACT_APP_MAGMA_URL || "/api/magma/thesis";

const FAKE_BASKETS = [
  { id: "commodities",      title: "Commodities Exportadoras", subtitle: "Petróleo, mineração e celulose com receita em USD.",  tickers: ["PETR4", "VALE3", "PRIO3"] },
  { id: "energia-eletrica", title: "Energia Elétrica", subtitle: "Geração, transmissão e distribuição indexadas ao IPCA.",     tickers: ["ELET3", "CPLE6", "CMIG4", "EQTL3"] },
  { id: "bancos",           title: "Bancões", subtitle: "Os quatro maiores bancos por capitalização.",                          tickers: ["ITUB4", "BBDC4", "BBAS3", "SANB11"] },
];

export const BENCHMARKS = [
  { id: "bvsp",  label: "Ibovespa", symbol: "^BVSP", color: "#60a5fa" },
  { id: "dolar", label: "Dólar",    symbol: "BRL=X", color: "#fbbf24" },
  { id: "ouro",  label: "Ouro",     symbol: "GC=F",  color: "#a3e635" },
];

export const THESIS_BASKETS = FAKE_BASKETS.map((b) => ({
  ...b,
  methodology: b.id === "brasil-domestico"
    ? "Cesta equal-weight de empresas cuja receita depende >70% do mercado interno brasileiro. Premissa: ciclo de corte na Selic beneficia consumo e infraestrutura."
    : b.id === "commodities"
    ? "Exportadoras de minério, petróleo e celulose. Premissa: dólar forte e demanda chinesa resiliente sustentam preços em USD."
    : b.id === "energia-eletrica"
    ? "Geradoras, transmissoras e distribuidoras. Premissa: setor defensivo com contratos indexados ao IPCA e fluxo de caixa previsível."
    : "Os quatro maiores bancos por capitalização. Premissa: proxy do sistema financeiro brasileiro.",
  scenario: b.id === "brasil-domestico"
    ? "Corte acumulado de 300bps na Selic até dez/2026"
    : b.id === "commodities"
    ? "USD/BRL acima de 5.20 e minério >US$110/ton"
    : b.id === "energia-eletrica"
    ? "IPCA acima de 4.5% a.a. com estabilidade regulatória"
    : "Spread bancário estável com inadimplência controlada",
  constituents: b.tickers.map((t, ci) => ({
    ticker: t,
    weight: (100 / b.tickers.length).toFixed(1),
    sector: b.id === "bancos" ? "Financeiro"
      : b.id === "commodities" ? ["Petróleo", "Mineração", "Petróleo"][ci] || "Commodities"
      : b.id === "energia-eletrica" ? "Energia"
      : ["Financeiro", "Financeiro", "Consumo", "Indústria", "Logística"][ci] || "Outros",
  })),
}));

async function fetchCandles(symbol) {
  const res = await fetch(`${API_BASE}/api/yahoo/v2/${encodeURIComponent(symbol)}`);
  if (!res.ok) throw new Error(`${symbol}: HTTP ${res.status}`);
  const data = await res.json();
  return (data.candles || []).filter((c) => c.close > 0);
}

export function useThesisChart(constituentTickers, activeBenchmarks) {
  const [chartData, setChartData] = useState([]);
  const [returns,   setReturns]   = useState({ daily: 0, weekly: 0, monthly: 0, m3: 0, yearly: 0 });
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  const tickerKey    = constituentTickers.join(",");
  const benchSymbols = activeBenchmarks.map((b) => b.symbol);

  useEffect(() => {
    if (!constituentTickers.length) return;
    let cancelled = false;
    setLoading(true); setError(null);

    Promise.all(
      [...constituentTickers, ...benchSymbols].map((sym) =>
        fetchCandles(sym).then((c) => ({ sym, candles: c, ok: true })).catch((e) => ({ sym, candles: [], ok: false, err: e.message }))
      )
    ).then((results) => {
      if (cancelled) return;
      const constR = results.filter((r) => constituentTickers.includes(r.sym));
      const benchR = results.filter((r) => benchSymbols.includes(r.sym));
      const validC = constR.filter((r) => r.candles.length > 0);
      if (!validC.length) { setError("Sem dados dos constituintes"); setLoading(false); return; }

      const dateSets = validC.map((r) => new Set(r.candles.map((c) => c.date)));
      const allDates = [...dateSets[0]].filter((d) => dateSets.every((s) => s.has(d))).sort();
      if (allDates.length < 2) { setError("Datas insuficientes"); setLoading(false); return; }

      const baseDate  = allDates[0];
      const constMaps = validC.map((r) => { const m = {}; r.candles.forEach((c) => { m[c.date] = c.close; }); return { sym: r.sym, m, base: m[baseDate] }; });
      const benchMaps = benchR.map((r) => {
        const m = {}; r.candles.forEach((c) => { m[c.date] = c.close; });
        const lookup = (date) => { if (m[date]) return m[date]; for (let d = 1; d <= 60; d++) { const p = new Date(date); p.setDate(p.getDate() - d); const k = p.toISOString().slice(0, 10); if (m[k]) return m[k]; } return null; };
        const info = activeBenchmarks.find((b) => b.symbol === r.sym);
        return { ...info, lookup, base: lookup(baseDate) };
      });

      const rows = allDates.map((date) => {
        const row    = { date };
        const levels = constMaps.map((c) => (c.base && c.m[date] ? (c.m[date] / c.base) * 100 : null)).filter((v) => v !== null);
        row.level    = levels.length ? levels.reduce((a, b) => a + b, 0) / levels.length : null;
        benchMaps.forEach((b) => { if (!b.base) return; const close = b.lookup(date); if (close) row[b.id] = (close / b.base) * 100; });
        return row;
      });

      const last = rows[rows.length - 1]?.level;
      const pct  = (a, b) => (a && b ? ((a - b) / b) * 100 : 0);
      setReturns({ daily: pct(last, rows[rows.length-2]?.level), weekly: pct(last, rows[rows.length-6]?.level), monthly: pct(last, rows[rows.length-22]?.level), m3: pct(last, rows[rows.length-64]?.level), yearly: pct(last, rows[rows.length-253]?.level) });
      setChartData(rows);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tickerKey, benchSymbols.join(",")]);

  return { chartData, returns, loading, error };
}